import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AdminService} from "../../../admin-panel/services/admin.service";
import {ActivatedRoute, Router} from "@angular/router";
import {switchMap} from "rxjs/operators";
import Swal from "sweetalert2";

@Component({
  selector: 'app-editor-edit-news',
  templateUrl: './editor-edit-news.component.html',
  styleUrls: ['./editor-edit-news.component.scss']
})
export class EditorEditNewsComponent implements OnInit {
  newsId: string = '';
  albanianForm: FormGroup;
  englishForm: FormGroup;
  selectionForm: FormGroup;
  publishDateForm: FormGroup;
  categories: any[] = [];
  mainImageFile: File | null = null;
  additionalImageFiles: File[] = [];
  mainImagePreviewUrl: string | null = null;
  additionalImagePreviewUrls: string[] = [];

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    this.albanianForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required]
    });

    this.englishForm = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required]
    });

    this.selectionForm = this.fb.group({
      categoryId: [null],
      subCategoryId: [null],
      nestedSubCategoryId: [null]
    });

    this.publishDateForm = this.fb.group({
      date: ['', Validators.required],
      time: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadCategories();
    this.activatedRoute.paramMap.subscribe(params => {
      this.newsId = params.get('id') || '';
      if (this.newsId) {
        this.loadNewsData();
      }
    });

    this.selectionForm.get('categoryId')?.valueChanges.subscribe(() => {
      this.selectionForm.patchValue({subCategoryId: null, nestedSubCategoryId: null});
    });
    this.selectionForm.get('subCategoryId')?.valueChanges.subscribe(() => {
      this.selectionForm.patchValue({nestedSubCategoryId: null});
    });
  }

  loadCategories(): void {
    this.adminService.getAllCategoriesWithTopLevel().subscribe((res) => {
      this.categories = res;
    });
  }

  loadNewsData(): void {
    this.adminService.getNewsById(this.newsId).subscribe((news: any) => {
      this.albanianForm.patchValue({
        title: news.alTitle,
        description: news.alDescription,
      });

      this.englishForm.patchValue({
        title: news.enTitle,
        description: news.enDescription,
      });

      this.selectionForm.patchValue({
        categoryId: news.category?.id || null,
        subCategoryId: news.subcategory?.id || null,
        nestedSubCategoryId: news.childSubcategory?.id || null,
      });

      const publishedDate = new Date(news.publishedDate);
      const pad = (n: number) => n.toString().padStart(2, '0');
      this.publishDateForm.patchValue({
        date: `${publishedDate.getFullYear()}-${pad(publishedDate.getMonth() + 1)}-${pad(publishedDate.getDate())}`,
        time: `${pad(publishedDate.getHours())}:${pad(publishedDate.getMinutes())}`,
      });

      this.mainImagePreviewUrl = news.mainImageUrl;
      this.additionalImagePreviewUrls = news.additionalImageUrls || [];

      this.mainImageFile = null;
      this.additionalImageFiles = [];
    });
  }

  getSubCategories() {
    const categoryId = this.selectionForm.get('categoryId')?.value;
    const category = this.categories.find((c) => c.id === categoryId);
    return category ? category.subCategories : [];
  }

  getNestedSubCategories() {
    const subCategoryId = this.selectionForm.get('subCategoryId')?.value;
    const subCategories = this.getSubCategories();
    const subCategory = subCategories.find((sub: any) => sub.id === subCategoryId);
    return subCategory ? subCategory.subCategories : [];
  }

  onMainImageChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.mainImageFile = input.files[0];
      this.mainImagePreviewUrl = URL.createObjectURL(this.mainImageFile);
    }
  }

  onAdditionalImagesChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.additionalImageFiles = Array.from(input.files);
      this.additionalImagePreviewUrls = this.additionalImageFiles.map((file) =>
        URL.createObjectURL(file)
      );
    }
  }

  onSubmit(): void {
    if (this.albanianForm.valid && this.englishForm.valid && this.selectionForm.valid && this.publishDateForm.valid) {
      // Prepare the data for the update request
      const dateValue = this.publishDateForm.get('date')?.value;
      const timeValue = this.publishDateForm.get('time')?.value;
      const [hours, minutes] = timeValue.split(':');

      const publishedDate = new Date(dateValue);
      publishedDate.setHours(Number(hours), Number(minutes));

      const updatedData = {
        alTitle: this.albanianForm.get('title')?.value,
        alDescription: this.albanianForm.get('description')?.value,
        enTitle: this.englishForm.get('title')?.value,
        enDescription: this.englishForm.get('description')?.value,
        categoryId: this.selectionForm.get('categoryId')?.value,
        subcategoryId: this.selectionForm.get('subCategoryId')?.value,
        childSubcategoryId: this.selectionForm.get('nestedSubCategoryId')?.value,
        publishedDate: publishedDate.toISOString(),
        mainImageUrl: this.mainImagePreviewUrl,
        additionalImageUrls: this.additionalImagePreviewUrls,
      };

      this.adminService.updateNews(this.newsId, updatedData).pipe(
        switchMap(() => {
          return this.adminService.postMainImage(this.newsId, this.mainImageFile || new File([], ''));
        }),
        switchMap(() => {
          return this.adminService.postAdditionalImages(this.newsId, this.additionalImageFiles.length > 0 ? this.additionalImageFiles : []);
        })
      ).subscribe({
        next: () => {
          Swal.fire({
            title: 'Success!',
            text: 'News updated successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            this.router.navigate(['/admin-panel/admin-news']);
          });
        },
        error: (err) => {
          console.error('Error during update or image upload:', err);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while updating the news.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        },
      });
    } else {
      // Handle invalid forms
      console.error('Forms are invalid.');
    }
  }
}
